import React, { useMemo, useState, useEffect, useRef } from 'react';
import jsonLogic from 'json-logic-js';
import {
  Modal,
  SpaceBetween,
  FormField,
  ColumnLayout,
  Container,
  Header,
  TextContent,
  Box,
  Button,
  Input,
  Select,
  DatePicker,
  Toggle,
  Popover,
  Icon,
  Table,
  Alert,
} from '@cloudscape-design/components';
import { AttributeMetadata } from '@amzn/controllershiphub-typescript-client';
import { previewDerivedUpdates } from 'src/utils/ControlSummaryUtils';

interface PreviewScenario {
  label?: string;
  value?: string;
  trigger?: string[];
  enabledExpression?: string;
  isButtonVisible?: boolean;
}

export const findSelectedEnum = (metadata: AttributeMetadata, value: any) =>
  metadata.enumAttributes?.find((attr) => attr.value === value) ?? null;

interface DataSetDetailsModalProps {
  visible: boolean;
  onDismiss: () => void;
  rowData: Record<string, any> | null;
  attributesMetadata?: AttributeMetadata[];
  onSave?: (updatedData: Record<string, any>) => Promise<void>;
  controlId: string;
  controlDatasetKey?: string;
  previewScenarios?: PreviewScenario[];
  defaultFilters?: string;
}

export const renderInputComponent = (
  metadata: AttributeMetadata,
  value: any,
  fieldKey: string,
  handleChange: (value: string) => void
) => {
  switch (metadata.dataType) {
    case 'java.lang.Enum':
      const selectedEnum = findSelectedEnum(metadata, value);
      return (
        <Select
          selectedOption={selectedEnum}
          onChange={({ detail }) => handleChange(detail.selectedOption?.value || '')}
          options={
            metadata.enumAttributes?.map((attr) => ({
              label: attr.label,
              value: attr.value,
            })) || []
          }
          placeholder={`Select ${fieldKey
            .replace(/([A-Z])/g, ' $1')
            .trim()
            .toLowerCase()}`}
        />
      );

    case 'java.time.LocalDate':
      return (
        <DatePicker
          value={value || ''}
          onChange={({ detail }) => handleChange(detail.value)}
          placeholder="YYYY/MM/DD"
          isDateEnabled={() => true}
          expandToViewport
        />
      );

    case 'java.lang.Boolean':
      return (
        <Toggle
          checked={value === true || value === 'true'}
          onChange={({ detail }) => handleChange(detail.checked.toString())}
        />
      );

    default:
      return (
        <Input
          value={value || ''}
          onChange={({ detail }) => handleChange(detail.value)}
          placeholder={`Enter ${fieldKey
            .replace(/([A-Z])/g, ' $1')
            .trim()
            .toLowerCase()}`}
          type={metadata.dataType?.toLowerCase().includes('number') ? 'number' : 'text'}
        />
      );
  }
};

const EditableField: React.FC<{
  fieldKey: string;
  value: any;
  metadata: AttributeMetadata;
  onChange: (key: string, value: any) => void;
}> = ({ fieldKey, value, metadata, onChange }) => {
  const handleChange = (newValue: string) => {
    onChange(fieldKey, newValue);
  };

  // Use enum label if applicable, else value OR null
  const selectedEnum = findSelectedEnum(metadata, value);
  // If the field is not editable, render as read-only
  if (!metadata.isEditable) {
    return (
      <FormField key={fieldKey} label={metadata.displayName} stretch={true}>
        <TextContent>
          <div
            style={{
              maxHeight: typeof value === 'object' ? '200px' : 'auto',
              overflowY: typeof value === 'object' ? 'auto' : 'visible',
              whiteSpace: 'pre-wrap',
            }}
          >
            <p
              style={{
                margin: 0,
                padding: '8px',
                backgroundColor: '#f8f8f8',
                borderRadius: '4px',
                minHeight: '32px',
                wordBreak: 'break-word',
              }}
            >
              {selectedEnum ? selectedEnum.label : (value ?? null)}
            </p>
          </div>
        </TextContent>
      </FormField>
    );
  }

  return (
    <FormField
      key={fieldKey}
      label={
        <>
          {metadata.displayName + ' '}
          {metadata.labelHint && (
            <Popover content={metadata.labelHint}>
              <Icon name="status-info" variant="link" />
            </Popover>
          )}
        </>
      }
      stretch={true}
    >
      {renderInputComponent(metadata, value, fieldKey, handleChange)}
    </FormField>
  );
};

const applyDynamicMetadataUpdates = (
  attributesMetadata: AttributeMetadata[] | undefined,
  rowData: { old: Record<string, any>; new: Record<string, any> }
): AttributeMetadata[] => {
  if (!attributesMetadata) return [];

  return attributesMetadata
    .filter((attr) => {
      if (!attr.visibilityExpression) return true;

      try {
        const logic = JSON.parse(attr.visibilityExpression);
        return jsonLogic.apply(logic, rowData);
      } catch (error) {
        console.error(`Invalid visibilityExpression for field ${attr.name}:`, error);
        return true;
      }
    })
    .map((attr) => {
      if (!attr.editabilityExpression) return attr;
      try {
        const logic = JSON.parse(attr.editabilityExpression);
        const isEditable = jsonLogic.apply(logic, rowData);
        return { ...attr, isEditable };
      } catch (error) {
        console.error(`Invalid editabilityExpression for field ${attr.name}:`, error);
        return attr;
      }
    });
};

export const DataSetDetailsModal: React.FC<DataSetDetailsModalProps> = ({
  visible,
  onDismiss,
  rowData,
  attributesMetadata,
  onSave,
  controlId,
  controlDatasetKey,
  previewScenarios,
  defaultFilters,
}) => {
  if (!rowData) return null;
  const [editedData, setEditedData] = useState<Record<string, any>>(rowData);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [changedFields, setChangedFields] = useState<Set<string>>(new Set());
  const [isPreviewing, setIsPreviewing] = useState<string | null>(null);
  const [previewMessage, setPreviewMessage] = useState<string | null>(null);
  const editedDataRef = useRef<Record<string, any>>(rowData);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const dynamicAttributesMetadata = useMemo(() => {
    return applyDynamicMetadataUpdates(attributesMetadata, { new: editedData, old: rowData });
  }, [attributesMetadata, editedData, rowData]);

  // Reset state when modal is opened with new data
  React.useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setEditedData(rowData);
    editedDataRef.current = rowData;
    setHasChanges(false);
    setIsSaving(false);
    setChangedFields(new Set());
    setIsPreviewing(null);
    setPreviewMessage(null);
  }, [rowData]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const handleFieldChange = (key: string, value: any) => {
    setEditedData((prev) => ({
      ...prev,
      [key]: value,
    }));
    setChangedFields((prev) => {
      const newSet = new Set(prev);
      if (value !== rowData[key]) {
        newSet.add(key);
      } else {
        newSet.delete(key);
      }
      return newSet;
    });
    setHasChanges(true);

    // Check if field triggers a preview scenario
    editedDataRef.current = { ...editedDataRef.current, [key]: value };
    const scenario = previewScenarios?.find((s) => s.trigger?.includes(key));
    if (scenario?.value) {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        const latestData = editedDataRef.current;
        handlePreview(scenario.value!, latestData);
      }, 500);
    }
  };

  const handleSave = async () => {
    if (!onSave) return;
    setIsSaving(true);

    try {
      const visibleFieldNames = new Set(dynamicAttributesMetadata.map((attr) => attr.name));

      // Create updated data object with only changed fields
      const updatedData = { ...rowData };
      const visibleChangedFields = Array.from(changedFields).filter((key) =>
        visibleFieldNames.has(key)
      );
      visibleChangedFields.forEach((key) => {
        updatedData[key] = editedData[key];
      });

      // Check if there are actual changes
      if (visibleChangedFields.length === 0) {
        throw new Error('No changes detected');
      }

      await onSave(updatedData).then(() => {
        setHasChanges(false);
        setChangedFields(new Set());
        onDismiss();
      });
    } catch (error) {
      console.error('Error saving changes:', error);
      alert(error instanceof Error ? error.message : String(error));
      // Reset to original data on save failure
      setEditedData(rowData);
      setHasChanges(false);
      setChangedFields(new Set());
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        onDismiss();
      }
    } else {
      onDismiss();
    }
  };

  const handlePreview = async (scenario: string, dataOverride?: Record<string, any>) => {
    if (!controlDatasetKey) return;
    setIsPreviewing(scenario);

    const data = dataOverride ?? editedData;
    try {
      const controlData = Object.entries(data)
        .filter(([name]) => name !== 'id')
        .map(([name, value]) => ({ name, value: String(value) }));

      const rowKey = data[controlDatasetKey];
      const result = await previewDerivedUpdates(controlId, rowKey, scenario, controlData);

      setPreviewMessage(result.previewMessage || null);

      setEditedData((prev) => {
        const updated = { ...prev, ...result.updatedFields };
        editedDataRef.current = updated;
        return updated;
      });

      const previewKeys = Object.keys(result.updatedFields);
      if (previewKeys.length > 0) {
        setChangedFields((prev) => {
          const newSet = new Set(prev);
          previewKeys.forEach((key) => newSet.add(key));
          return newSet;
        });
        setHasChanges(true);
      }
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Preview failed');
    } finally {
      setIsPreviewing(null);
    }
  };

  const isScenarioEnabled = (scenario: PreviewScenario): boolean => {
    if (!scenario.enabledExpression) return true;
    try {
      const logic = JSON.parse(scenario.enabledExpression);
      return jsonLogic.apply(logic, { new: editedData, old: rowData });
    } catch (error) {
      console.error(`Invalid enabledExpression for scenario ${scenario.value}:`, error);
      return true;
    }
  };

  const groupedFields = useMemo(() => {
    const fieldGroups = new Map<
      string,
      Array<{
        key: string;
        value: string | number | null;
        metadata: AttributeMetadata;
      }>
    >();

    dynamicAttributesMetadata?.forEach((attributeMetadata) => {
      const groupName = attributeMetadata.groupName || 'Other Information';
      if (!fieldGroups.has(groupName)) {
        fieldGroups.set(groupName, []);
      }
      fieldGroups.get(groupName)?.push({
        key: attributeMetadata.name!,
        value: editedData[attributeMetadata.name!] || null,
        metadata: attributeMetadata,
      });
    });

    // Calculating and adding last numbered bullet point to group: Other Information
    const totalGroups = fieldGroups.size;
    const entries = Array.from(fieldGroups.entries()).map(([groupName, fields]) => {
      const displayName =
        groupName === 'Other Information' ? `${totalGroups}. Other Information` : groupName;
      return [displayName, fields] as [string, typeof fields];
    });

    return entries.sort((firstField, secondField) => {
      const firstFieldIsRequired = firstField[0].toLowerCase().includes('required');
      const secondFieldIsRequired = secondField[0].toLowerCase().includes('required');
      if (firstFieldIsRequired && !secondFieldIsRequired) return -1;
      if (!firstFieldIsRequired && secondFieldIsRequired) return 1;
      return firstField[0].localeCompare(secondField[0]);
    });
  }, [editedData, dynamicAttributesMetadata]);

  const detailedViewConfig = useMemo(() => {
    try {
      return defaultFilters ? JSON.parse(defaultFilters).detailedViewConfig || {} : {};
    } catch {
      return {};
    }
  }, [defaultFilters]);

  const renderTableGroup = (
    groupName: string,
    fields: Array<{ key: string; value: any; metadata: AttributeMetadata }>,
    config: { tableColumns: string[]; columns?: number }
  ) => {
    const suffixes = config.tableColumns.map((c) => c.charAt(0).toUpperCase() + c.slice(1));
    const primarySuffix = suffixes[0];
    const secondarySuffix = suffixes[1];

    const regularFields: typeof fields = [];
    const tableFields: typeof fields = [];
    const pairedKeys = new Set<string>();

    fields.forEach((field) => {
      if (
        field.key.endsWith(primarySuffix) &&
        fields.some(
          (f) => f.key === field.key.replace(new RegExp(`${primarySuffix}$`), secondarySuffix)
        )
      ) {
        tableFields.push(field);
        pairedKeys.add(field.key);
        pairedKeys.add(field.key.replace(new RegExp(`${primarySuffix}$`), secondarySuffix));
      }
    });

    fields.forEach((field) => {
      if (!pairedKeys.has(field.key)) {
        regularFields.push(field);
      }
    });

    const tableItems = tableFields.map((primaryField) => {
      const secondaryKey = primaryField.key.replace(
        new RegExp(`${primarySuffix}$`),
        secondarySuffix
      );
      const secondaryField = fields.find((f) => f.key === secondaryKey);
      return {
        milestone: (primaryField.metadata.displayName ?? primaryField.key).replace(
          new RegExp(` ${primarySuffix}$`),
          ''
        ),
        primary: {
          value: primaryField.value ?? '-',
          key: primaryField.key,
          metadata: primaryField.metadata,
        },
        secondary: {
          value: secondaryField?.value ?? '-',
          key: secondaryKey,
          metadata: secondaryField?.metadata,
        },
      };
    });

    return (
      <Container header={<Header variant="h2">{groupName}</Header>}>
        <SpaceBetween size="l">
          {regularFields.length > 0 && (
            <ColumnLayout columns={config.columns || regularFields.length} variant="text-grid">
              {regularFields.map(({ key, value, metadata }) => (
                <EditableField
                  key={key}
                  fieldKey={key}
                  value={value}
                  metadata={metadata}
                  onChange={handleFieldChange}
                />
              ))}
            </ColumnLayout>
          )}
          <Table
            variant="embedded"
            items={tableItems}
            columnDefinitions={[
              {
                id: 'milestone',
                header: 'Milestone',
                cell: (item) => (
                  <>
                    {item.milestone}
                    {item.primary.metadata?.labelHint && (
                      <Box color="text-status-inactive" display="inline" margin={{ left: 'xs' }}>
                        <i>[{item.primary.metadata.labelHint}]</i>
                      </Box>
                    )}
                  </>
                ),
              },
              {
                id: suffixes[0].toLowerCase(),
                header: suffixes[0],
                cell: (item) =>
                  item.primary.metadata?.isEditable ? (
                    <EditableField
                      fieldKey={item.primary.key}
                      value={item.primary.value}
                      metadata={item.primary.metadata}
                      onChange={handleFieldChange}
                    />
                  ) : (
                    item.primary.value
                  ),
              },
              {
                id: suffixes[1].toLowerCase(),
                header: suffixes[1],
                cell: (item) =>
                  item.secondary.metadata?.isEditable ? (
                    <EditableField
                      fieldKey={item.secondary.key}
                      value={item.secondary.value}
                      metadata={item.secondary.metadata!}
                      onChange={handleFieldChange}
                    />
                  ) : (
                    item.secondary.value
                  ),
              },
            ]}
          />
        </SpaceBetween>
      </Container>
    );
  };

  const renderGroup = (
    groupName: string,
    fields: Array<{
      key: string;
      value: any;
      metadata: AttributeMetadata;
    }>
  ) => {
    const config = detailedViewConfig[groupName];
    if (config?.renderAs === 'table' && config.tableColumns?.length === 2) {
      return renderTableGroup(groupName, fields, config);
    }

    return (
      <Container header={<Header variant="h2">{groupName}</Header>}>
        <ColumnLayout columns={config?.columns || 3} variant="text-grid">
          {fields.map(({ key, value, metadata }) => (
            <EditableField
              key={key}
              fieldKey={key}
              value={value}
              metadata={metadata}
              onChange={handleFieldChange}
            />
          ))}
        </ColumnLayout>
      </Container>
    );
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleCancel}
      header={<Header variant="h2">Detailed View</Header>}
      size="max"
      className="custom-modal"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
            {previewScenarios
              ?.filter((scenario) => scenario.isButtonVisible)
              .map((scenario) => {
                const isEnabled = isScenarioEnabled(scenario);
                return (
                  <Button
                    key={scenario.value}
                    onClick={() => handlePreview(scenario.value!)}
                    loading={isPreviewing === scenario.value}
                    disabled={isSaving || isPreviewing !== null || !isEnabled}
                  >
                    {scenario.label}
                  </Button>
                );
              })}
            <Button
              variant="primary"
              onClick={handleSave}
              loading={isSaving}
              disabled={!hasChanges || isSaving}
            >
              Save changes
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      {previewMessage && (
        <div style={{ position: 'sticky', top: 0, zIndex: 1000, marginBottom: '16px' }}>
          <Alert type="info" dismissible onDismiss={() => setPreviewMessage(null)}>
            {previewMessage}
          </Alert>
        </div>
      )}
      <Box padding={{ horizontal: 'l' }}>
        <SpaceBetween size="l">
          {groupedFields.map(([groupName, fields]) => (
            <Box key={groupName}>{renderGroup(groupName, fields)}</Box>
          ))}
        </SpaceBetween>
      </Box>
    </Modal>
  );
};
